import React, { useEffect, useState, useMemo } from "react";
import { Box, Tooltip, Typography, Checkbox, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  formattedJson,
  getFormattedDateTime,
  removeCheckboxFromJson,
} from "../../services/utils";
import { useParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import AuditComponent from "../../components/AuditComponent";
import { useResourceContext } from "../../contexts/ResourceContext";
import useFetchRuleBackup from "../../hooks/useFetchRuleBackup";
import { IconCompareReportSvg, IconReportsSvg } from "../../common/utils/icons";
import useFetchRuleById from "../../hooks/useFetchRuleById";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { getConfig } from "../../config/runtimeConfig";

const AuditRule = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { id }: any = useParams();
  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [auditSource, setAuditSource] = useState("");
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedInfoData, setSelectedInfoData] = useState<any>(null);

  const [ruleBackupData] = useFetchRuleBackup({
    ruleId: id,
    setIsLoading,
    page,
    pSize,
  });
  // const { domainsData } = useResourceContext();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        flex: 0,
        minWidth: 0,
        renderCell: (params: any) => null,
      },
      {
        field: "checkbox",
        headerName: "",
        width: 80,
        renderCell: (params) => {
          const isSelected = selectedRows.some(
            (row) => row.id === params?.row?.id,
          );
          return (
            <Box sx={{ paddingTop: "4px" }}>
              <Checkbox
                checked={isSelected}
                onChange={() => toggleSelection(params?.row)}
                sx={{
                  "&.Mui-checked": {
                    color: "#FFA500",
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "domain_name",
        headerName: "Domain Name",
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
      {
        field: "code",
        headerName: "Code",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
      {
        field: "created_date",
        headerName: "Last Modified",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          return getFormattedDateTime(params.value);
        },
      },
      {
        field: "modified_by_user",
        headerName: "Modified By",
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value?.username} />;
        },
      },
      {
        field: "comment",
        headerName: "Comments",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
          return <LimitChractersWithTooltip value={params?.value || ""} />;
        },
      },
      {
        field: "info",
        headerName: "Rule Info",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => {
          const { checkbox, ...rowWithoutCheckbox } = params.row;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tooltip
                componentsProps={{
                  tooltip: { className: "wide-tooltip w-380" },
                }}
                title={
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <React.Fragment>
                      <Typography color="inherit">Rule Info</Typography>

                      <Typography>
                        {formattedJson(JSON.stringify(rowWithoutCheckbox))}
                      </Typography>
                    </React.Fragment>
                  </pre>
                }
              >
                <InfoIcon
                  sx={{
                    width: "16px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
              <Tooltip title="Expand to view full details" placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedInfoData(rowWithoutCheckbox);
                    setOpenInfoDialog(true);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(255, 165, 0, 0.1)",
                    },
                  }}
                >
                  <OpenInFullIcon sx={{ width: "16px", height: "16px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 130,
        renderCell: (params: any) => {
          return (
            <>
              <Tooltip title="Compare Current Definition" placement="top">
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    compareCurrentItem(params?.row, "currentCompare")
                  }
                >
                  <IconCompareReportSvg />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [selectedRows],
  );

  const [ruleData] = useFetchRuleById({ setIsLoading, currentRuleId: id });
  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: ruleData?.name,
      id: ruleData?.id,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [ruleData]);

  const compareCurrentItem = async (data: any, source: any) => {
    const newData = await removeCheckboxFromJson(data);
    if (selectedRows.length > 0) {
      setSelectedRows([]);
    }
    setSelectedRows((prevSelectedRows) => [
      ...prevSelectedRows,
      newData,
      ruleData,
    ]);
    setAuditSource(source);
    setOpenDialog(true);
  };

  const toggleSelection = (row: { id: any }) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.some((selectedRow) => selectedRow.id === row.id)) {
        // Deselect row
        return prevSelectedRows.filter(
          (selectedRow) => selectedRow.id !== row.id,
        );
      } else {
        // Select row
        if (prevSelectedRows.length < 2) {
          return [...prevSelectedRows, removeCheckboxFromJson(row)];
        } else {
          // Replace the second selected row with the new row
          return [prevSelectedRows[0], removeCheckboxFromJson(row)];
        }
      }
    });
  };

  return (
    <>
      <AuditComponent
        auditColumnData={columns || []}
        auditRowData={ruleBackupData || []}
        isLoading={isLoading}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        toggleSelection={toggleSelection}
        dataListTitle="Rule Modification History"
        page={page}
        setPage={setPage}
        pSize={pSize}
        setPSize={setPSize}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        auditSource={auditSource}
        setAuditSource={setAuditSource}
        singlePageMaxHeightDiff={333}
      />
      <ViewPayloadDialog
        openDialog={openInfoDialog}
        dialogTitle="Rule Info"
        payload={selectedInfoData}
        handleDialogClose={() => setOpenInfoDialog(false)}
      />
    </>
  );
};

export default AuditRule;
